<template>
  <main class="flex-1 p-6 overflow-y-auto bg-secondary font-light">
    <div class="flex flex-row mb-6 justify-between">
      <div>
        <h1>
          Welcome back, <span class="font-serif italic">{{ authStore.user?.first_name }}!</span>
        </h1>
        <p class="mt-1">
          This is overview of your shopexperts expert dashboard.
        </p>
      </div>

      <div class="w-40">
        <UiSelect 
          v-model="timeRange" 
          :options="timeRangeOptions"
        />
      </div>
    </div>

    <LoadingCard :horizontal="true" v-if="isAnalyticsLoading" />
    <div v-else class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
      <StatsCard 
        title="Projects" 
        :value="analytics?.leads_count ?? 0" 
        description="Number of projects matched to your profile"
      />
      <StatsCard 
        title="CTA Clicks" 
        :value="analytics?.cta_clicks ?? 0" 
        description="'Get a Quote' clicks from your profile, listings, packaged services, and case studies"
      />
      <StatsCard 
        title="Impressions" 
        :value="analytics?.impressions ?? 0" 
        description="Times your card was displayed in listings, featured sections, and search results"
      />
      <StatsCard 
        title="Profile Views" 
        :value="analytics?.listing_visits ?? 0" 
        description="Views of your profile page"
      />
      <StatsCard 
        title="Unique Visitors" 
        :value="analytics?.unique_visits ?? 0" 
        description="Unique visitors to your profile page"
      />
      <StatsCard 
        title="Outbound Clicks" 
        :value="analytics?.outbound_clicks ?? 0" 
        description="Clicks on your website and social media links"
      />
    </div>

    <TabNav
        :tabs="tabs"
    />
    <WelcomeDialog v-model="showWelcomeDialog" />
  </main>
</template>

<script setup lang="ts">
import StatsCard from '../../components/common/cards/StatCard.vue'
import TabNav from "../../components/TabNav.vue";
import {ref, computed, onMounted} from "vue";
import LoadingCard from "@/components/common/LoadingCard.vue";
import WelcomeDialog from "@/components/expert/WelcomeDialog.vue";
import {useAuthStore} from "@/store/auth.ts";
import { useProfileStatus } from "@/pages/expert/listing/composables/useProfileStatus";
import { useExpertAnalytics } from "@/app/composables/useExpertAnalytics";
import { UiSelect } from "@shopexperts/shared";
import ProjectsPreviewPanel from "@/v2Projects/components/ProjectsPreviewPanel.vue";
import ExpertPublishedReviews from "@/app/pages/expert-reviews/ExpertPublishedReviews.vue";

const authStore = useAuthStore();
const timeRange = ref<'last_30_days' | 'all_time'>('last_30_days');
const timeRangeOptions: { label: string; value: string }[] = [
  { label: 'Last 30 Days', value: 'last_30_days' },
  { label: 'All Time', value: 'all_time' },
];

const analyticsQuery = useExpertAnalytics();
const isAnalyticsLoading = computed(() => analyticsQuery.isLoading.value);

const analytics = computed(() => {
  const data = analyticsQuery.data.value;
  if (!data) return null;
  
  const range = timeRange.value;
  return {
    leads_count: data.leads_count[range],
    cta_clicks: data.cta_clicks[range],
    impressions: data.impressions[range],
    listing_visits: data.listing_visits[range],
    unique_visits: data.unique_visits[range],
    outbound_clicks: data.outbound_clicks[range],
  };
});

const tabs = [
  { value: 'latest-projects', label: 'Active Projects', component: ProjectsPreviewPanel },
  { value: 'latest-reviews', label: 'Latest Reviews', component: ExpertPublishedReviews },
]

const { isDraft } = useProfileStatus();
const showWelcomeDialog = ref(false);

onMounted(() => {
  if (isDraft.value) {
    showWelcomeDialog.value = true;
  }
})
</script>
